import { send_request } from '../utils/Request';

/**
 * Agent Teams API client. Mirrors the TeamController (browser-authenticated) endpoints under
 * `ai-service/team` on integrations-service. Tenant identity is derived from the JWT server-side —
 * never passed in the body. Callers inspect `response?.data` (success) vs `response?.response`
 * (axios error), per the existing convention.
 */

export const TEAM_API_BASE = 'ai-service/team';

export const listTeams = async () => send_request(TEAM_API_BASE, '', {}, 'GET');

export const getTeam = async teamId =>
  send_request(`${TEAM_API_BASE}/${teamId}`, '', {}, 'GET');

export const createTeam = async body =>
  send_request(TEAM_API_BASE, body, {}, 'POST');

export const updateTeam = async (teamId, body) =>
  send_request(`${TEAM_API_BASE}/${teamId}`, body, {}, 'PUT');

export const deleteTeam = async teamId =>
  send_request(`${TEAM_API_BASE}/${teamId}`, '', {}, 'DELETE');

/** Seed a starting orchestration graph from a preset (not persisted). */
export const seedTeamPreset = async ({ presetKind, agents, options }) =>
  send_request(`${TEAM_API_BASE}/preset`, { presetKind, agents, options }, {}, 'POST');

// ── Use-case templates ────────────────────────────────────────────
// The team template catalog lives in the frontend (see teamTemplates.js), consistent with
// boards/tables. Instantiation is orchestrated client-side by teamCreationRunner.js using the
// existing agent-create + `POST /team/preset` + `POST /team` endpoints. Only the draft/publish
// lifecycle is server-owned.

/** Publish a draft team so it becomes chattable. Returns the updated team. */
export const publishTeam = async teamId =>
  send_request(`${TEAM_API_BASE}/${teamId}/publish`, {}, {}, 'POST');

// ── Team chat sessions ────────────────────────────────────────────
// The parent conversation is an ordinary agent session, so SSE streaming reuses the existing
// useChatStreamStore (already pointed at integrations-service by sessionId). Only list/start/detail
// are team-specific.

/** Start a new parent team conversation → `{ parentSessionId }`. */
export const startTeamSession = async teamId =>
  send_request(`${TEAM_API_BASE}/${teamId}/session`, {}, {}, 'POST');

/** List a team's parent conversations, newest first. */
export const listTeamSessions = async teamId =>
  send_request(`${TEAM_API_BASE}/${teamId}/sessions`, '', {}, 'GET');

/** Full parent transcript (session + visible messages). */
export const getTeamSession = async parentSessionId =>
  send_request(`${TEAM_API_BASE}/session/${parentSessionId}`, '', {}, 'GET');

/**
 * Post a user message to a team conversation. The orchestrator runs the graph server-side; replies
 * stream back over the existing session SSE, so callers only need to fire-and-forget here.
 */
export const postTeamMessage = async (parentSessionId, message) =>
  send_request(
    `${TEAM_API_BASE}/session/${parentSessionId}/message`,
    { message },
    {},
    'POST'
  );

/** Roster of a conversation joined with each member's child session id (for the roster strip). */
export const listTeamSessionMembers = async parentSessionId =>
  send_request(`${TEAM_API_BASE}/session/${parentSessionId}/members`, '', {}, 'GET');

/** Read-only transcript of a child worker session belonging to a team conversation (Session tab). */
export const getTeamChildSession = async (parentSessionId, childSessionId) =>
  send_request(
    `${TEAM_API_BASE}/session/${parentSessionId}/child/${childSessionId}`,
    '',
    {},
    'GET'
  );

/** Lightweight runtime state (run status + any pending wait_human gate) for a team conversation. */
export const getTeamSessionState = async parentSessionId =>
  send_request(`${TEAM_API_BASE}/session/${parentSessionId}/state`, '', {}, 'GET');

/**
 * Resolve a wait_human gate. `decision` is 'approve' | 'reject'; `editedContent` (optional) flows
 * forward as the draft. The orchestrator resumes server-side and streams over the session SSE.
 */
export const postTeamHumanDecision = async (parentSessionId, decision, editedContent) =>
  send_request(
    `${TEAM_API_BASE}/session/${parentSessionId}/human-decision`,
    { decision, editedContent },
    {},
    'POST'
  );
